import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { updatePageSEO, generateStructuredData } from '../../utils/seo';

const BlogPage: React.FC = () => {
  useEffect(() => {
    const siteUrl = window.location.origin;
    updatePageSEO({
      title: 'ブログ・新着情報｜おかやま司法書士事務所 - 岡山市・倉敷市',
      description: '岡山市・倉敷市の司法書士事務所のブログ・新着情報。相続登記、不動産登記、商業登記、債務整理、成年後見に関する法務情報と事務所の最新ニュースをお届けします。法改正情報や実務に役立つ情報を定期更新。',
      keywords: 'ブログ,新着情報,法務情報,相続登記,不動産登記,商業登記,債務整理,成年後見,岡山市,倉敷市',
      canonical: `${siteUrl}/blog`,
      ogTitle: 'ブログ・新着情報｜おかやま司法書士事務所',
      ogDescription: '岡山市・倉敷市の司法書士事務所のブログ・新着情報。法務情報と事務所の最新ニュースをお届けします。',
      ogUrl: `${siteUrl}/blog`,
      structuredData: generateStructuredData.blog()
    });
  }, []);

  const blogPosts = [
    {
      id: 1,
      title: '相続登記の義務化について',
      date: '2024年3月15日',
      category: '相続手続き',
      excerpt: '2024年4月1日より相続登記が義務化されました。相続が発生した場合の手続きについて詳しく解説いたします。',
      image: 'https://public.readdy.ai/ai/img_res/43bf9da9d8b31af418a94caff0e0e43f.jpg'
    },
    {
      id: 2,
      title: '不動産売買時の登記手続きの流れ',
      date: '2024年3月10日',
      category: '不動産登記',
      excerpt: '不動産の売買契約から登記完了までの流れと、必要な書類について分かりやすく説明いたします。',
      image: 'https://readdy.ai/api/search-image?query=Modern%20Japanese%20house%20with%20for%20sale%20sign%2C%20real%20estate%20transaction%20concept%2C%20bright%20daylight%2C%20residential%20property%2C%20clean%20architecture%2C%20no%20text%20visible&width=400&height=250&seq=blog2&orientation=landscape'
    },
    {
      id: 3,
      title: '会社設立時の商業登記について',
      date: '2024年3月5日',
      category: '商業登記',
      excerpt: '新しく会社を設立する際の商業登記手続きと、必要な準備について詳しくご案内いたします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20business%20startup%20meeting%2C%20entrepreneurs%20planning%20new%20company%2C%20modern%20office%20interior%2C%20documents%20and%20laptop%20on%20table%2C%20professional%20atmosphere%2C%20no%20text%20visible&width=400&height=250&seq=blog3&orientation=landscape'
    },
    {
      id: 4,
      title: '成年後見制度の活用方法',
      date: '2024年2月28日',
      category: '成年後見',
      excerpt: '認知症や知的障害により判断能力が不十分な方を支援する成年後見制度について解説いたします。',
      image: 'https://readdy.ai/api/search-image?query=Caring%20Japanese%20caregiver%20supporting%20elderly%20person%2C%20warm%20family%20consultation%20scene%2C%20comfortable%20home%20environment%2C%20natural%20lighting%2C%20compassionate%20atmosphere%2C%20no%20text%20visible&width=400&height=250&seq=blog4&orientation=landscape'
    },
    {
      id: 5,
      title: '債務整理の種類と選択方法',
      date: '2024年2月20日',
      category: '債務整理',
      excerpt: '借金問題でお悩みの方へ、任意整理・個人再生・自己破産の違いと選択のポイントをご説明します。',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Japanese%20consultation%20meeting%2C%20advisor%20helping%20client%20with%20financial%20documents%2C%20supportive%20atmosphere%2C%20modern%20office%20setting%2C%20natural%20lighting%2C%20no%20text%20visible&width=400&height=250&seq=blog5&orientation=landscape'
    },
    {
      id: 6,
      title: '遺言書作成のポイント',
      date: '2024年2月15日',
      category: '相続手続き',
      excerpt: '将来のトラブルを防ぐための遺言書作成のポイントと、自筆証書遺言と公正証書遺言の違いについて。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20elderly%20person%20writing%20important%20documents%2C%20peaceful%20home%20study%20room%2C%20natural%20lighting%2C%20traditional%20and%20modern%20elements%2C%20contemplative%20atmosphere%2C%20no%20text%20visible&width=400&height=250&seq=blog6&orientation=landscape'
    }
  ];

  const news = [
    {
      id: 1,
      title: '年末年始の営業について',
      date: '2024年3月20日',
      type: 'お知らせ'
    },
    {
      id: 2,
      title: '司法書士津山次郎が入所いたしました',
      date: '2024年3月18日',
      type: 'スタッフ'
    },
    {
      id: 3,
      title: '相続登記義務化に関する無料相談会開催',
      date: '2024年3月12日',
      type: 'イベント'
    },
    {
      id: 4,
      title: 'ホームページをリニューアルいたしました',
      date: '2024年3月8日',
      type: 'お知らせ'
    },
    {
      id: 5,
      title: '岡山商工会議所主催セミナーに登壇',
      date: '2024年3月1日',
      type: 'メディア'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-purple-600 to-purple-800">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=Modern%20Japanese%20office%20workspace%20with%20computer%20and%20documents%2C%20professional%20blog%20writing%20environment%2C%20natural%20lighting%2C%20clean%20desk%20setup%2C%20contemporary%20interior%20design%2C%20no%20text%20visible&width=1920&height=600&seq=blog-hero&orientation=landscape"
              alt="ブログ・新着情報"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-purple-900/70"></div>
          </div>
          <div className="relative z-10 container mx-auto px-4 text-center">
            <div className="flex justify-center mb-6 overflow-x-auto pb-2">
              <div className="flex space-x-2 px-4 min-w-max">
                <a href="/" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  ホーム
                </a>
                <a href="/about" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  会社概要
                </a>
                <a href="/recruit" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  求人情報
                </a>
                <a href="/blog" className="px-3 md:px-4 py-2 bg-white text-purple-600 rounded-full whitespace-nowrap text-xs md:text-sm">
                  ブログ
                </a>
              </div>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">ブログ・新着情報</h1>
            <p className="text-base md:text-xl text-gray-200 max-w-3xl mx-auto px-4">
              法的手続きに関する有用な情報と事務所の最新情報をお届けします
            </p>
          </div>
        </section>

        {/* Latest News */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">新着情報</h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-gray-50 rounded-lg p-5 md:p-8">
                <div className="space-y-3 md:space-y-4">
                  {news.map((item) => (
                    <div key={item.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 md:py-4 border-b border-gray-200 last:border-b-0 gap-2 sm:gap-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0">
                        <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">{item.date}</span>
                        <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium w-fit ${
                          item.type === 'お知らせ' ? 'bg-blue-100 text-blue-800' :
                          item.type === 'スタッフ' ? 'bg-green-100 text-green-800' :
                          item.type === 'イベント' ? 'bg-purple-100 text-purple-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          {item.type}
                        </span>
                        <h3 className="text-sm md:text-base text-gray-900 hover:text-purple-600 cursor-pointer transition-colors break-words">
                          {item.title}
                        </h3>
                      </div>
                      <i className="ri-arrow-right-line text-gray-400 hidden sm:block flex-shrink-0"></i>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">法務ブログ</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
              {blogPosts.map((post) => (
                <article key={post.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="h-40 md:h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <span className="px-2 md:px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full whitespace-nowrap">
                        {post.category}
                      </span>
                      <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">{post.date}</span>
                    </div>
                    <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3 hover:text-purple-600 cursor-pointer transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-3 md:mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center text-purple-600 hover:text-purple-700 cursor-pointer transition-colors">
                      <span className="text-xs md:text-sm font-medium">続きを読む</span>
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">カテゴリー別記事</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
              <div className="bg-blue-50 rounded-lg p-6 md:p-8 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-home-line text-xl md:text-2xl text-white"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">不動産登記</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 px-2">
                  不動産の売買、相続、贈与に関する登記手続きについて
                </p>
                <span className="text-blue-600 font-medium text-xs md:text-sm">8記事</span>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6 md:p-8 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-heart-line text-xl md:text-2xl text-white"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">相続手続き</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 px-2">
                  相続登記、遺言書作成、遺産分割に関する情報
                </p>
                <span className="text-green-600 font-medium text-xs md:text-sm">12記事</span>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-6 md:p-8 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-building-line text-xl md:text-2xl text-white"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">商業登記</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 px-2">
                  会社設立、役員変更、本店移転などの商業登記
                </p>
                <span className="text-purple-600 font-medium text-xs md:text-sm">6記事</span>
              </div>
              
              <div className="bg-orange-50 rounded-lg p-6 md:p-8 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-shield-check-line text-xl md:text-2xl text-white"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">債務整理</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 px-2">
                  任意整理、個人再生、自己破産に関する解説
                </p>
                <span className="text-orange-600 font-medium text-xs md:text-sm">5記事</span>
              </div>
              
              <div className="bg-red-50 rounded-lg p-6 md:p-8 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-user-heart-line text-xl md:text-2xl text-white"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">成年後見</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 px-2">
                  成年後見制度の活用方法と手続きについて
                </p>
                <span className="text-red-600 font-medium text-xs md:text-sm">4記事</span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 md:p-8 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-file-text-line text-xl md:text-2xl text-white"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">その他法務</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 px-2">
                  契約書作成、内容証明郵便などの法務サービス
                </p>
                <span className="text-gray-600 font-medium text-xs md:text-sm">7記事</span>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter Subscription */}
        <section className="py-12 md:py-20 bg-purple-50">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">メールマガジン登録</h2>
            <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 max-w-3xl mx-auto px-4">
              法改正情報や実務に役立つ情報を定期的にお届けします。
              ぜひメールマガジンにご登録ください。
            </p>
            
            <div className="max-w-md mx-auto px-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="メールアドレスを入力"
                  className="flex-1 px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg sm:rounded-l-lg sm:rounded-r-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
                />
                <button className="px-4 md:px-6 py-2 md:py-3 bg-purple-600 text-white font-medium rounded-lg sm:rounded-l-none sm:rounded-r-lg hover:bg-purple-700 transition-colors whitespace-nowrap text-sm md:text-base">
                  登録する
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                ※配信停止はいつでも可能です
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
